import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Segment, Button, Icon, Input } from 'semantic-ui-react';

import FieldEditor from './FieldEditor';
import styles from './SectionEditor.module.scss';

const SectionEditor = React.memo(({
  section,
  index,
  isSelected,
  isDragging,
  dragHandleProps,
  onSelect,
  onUpdate,
  onDelete,
  onAddField,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(section.title);

  const handleTitleSave = () => {
    onUpdate({ ...section, title: titleValue });
    setIsEditingTitle(false);
  };

  const handleUpdateField = (fieldIndex, updatedField) => {
    const newFields = [...section.fields];
    newFields[fieldIndex] = updatedField;
    onUpdate({ ...section, fields: newFields });
  };

  const handleDeleteField = (fieldIndex) => {
    if (window.confirm('Delete this field?')) {
      const newFields = section.fields.filter((_, i) => i !== fieldIndex);
      onUpdate({ ...section, fields: newFields });
    }
  };

  // Handle drag & drop de campos
  const handleFieldDragEnd = (result) => {
    if (!result.destination) return;

    const newFields = Array.from(section.fields);
    const [movedField] = newFields.splice(result.source.index, 1);
    newFields.splice(result.destination.index, 0, movedField);

    // Actualizar orders
    newFields.forEach((field, idx) => {
      field.order = idx + 1;
    });

    onUpdate({ ...section, fields: newFields });
  };

  return (
    <Segment
      className={`${styles.sectionCard} ${isSelected ? styles.selected : ''} ${isDragging ? styles.dragging : ''}`}
      onClick={onSelect}
    >
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          <div {...dragHandleProps} className={styles.dragHandle} title="Drag to reorder">
            <Icon name="ellipsis vertical" />
          </div>
          {isEditingTitle ? (
            <div className={styles.titleEdit}>
              <Input
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onBlur={handleTitleSave}
                onKeyPress={(e) => e.key === 'Enter' && handleTitleSave()}
                autoFocus
                size="small"
              />
              <Button size="tiny" icon="check" onClick={handleTitleSave} />
            </div>
          ) : (
            <>
              <h4>
                {section.title}
              </h4>
              <span className={styles.fieldCount}>
                ({section.fields.length} {section.fields.length === 1 ? 'field' : 'fields'})
              </span>
            </>
          )}
        </div>

        <div className={styles.sectionActions}>
          <Button
            size="tiny"
            icon="edit"
            basic
            title="Edit section"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(); // Expandir la sección
              setIsEditingTitle(true); // Y activar edición del título
            }}
          />
          <Button
            size="tiny"
            icon="trash"
            basic
            color="red"
            title="Delete section"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          />
        </div>
      </div>

      {/* Campos de la sección con drag & drop */}
      {isSelected && (
        <DragDropContext onDragEnd={handleFieldDragEnd}>
          <Droppable droppableId={`fields-${section.id}`} type={`field-${index}`}>
            {(provided) => (
              <div
                className={styles.fieldsContainer}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {section.fields.map((field, fieldIndex) => (
                  <Draggable
                    key={field.id}
                    draggableId={field.id}
                    index={fieldIndex}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <FieldEditor
                          field={field}
                          index={fieldIndex}
                          isDragging={snapshot.isDragging}
                          dragHandleProps={provided.dragHandleProps}
                          onUpdate={(updated) => handleUpdateField(fieldIndex, updated)}
                          onDelete={() => handleDeleteField(fieldIndex)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}

                <Button
                  size="small"
                  basic
                  fluid
                  icon
                  labelPosition="left"
                  onClick={(e) => { e.stopPropagation(); onAddField(); }}
                  className={styles.addFieldButton}
                >
                  <Icon name="plus" />
                  Add Field
                </Button>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Segment>
  );
});

SectionEditor.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool,
  dragHandleProps: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAddField: PropTypes.func.isRequired,
};

export default SectionEditor;
